import { last, omit, pick } from 'lodash';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import type { RootStore } from '~/stores/root.store';
import {
    CoreInboundEvents,
    CoreOutboundEvents,
    GeneralOutboundEvents
} from '~/components/Builder/Mobile/builderMobileApi.enums';
import { TBlock, TBlockCreationDirection } from '~/types/builder.types';
import { getNewBlockOrder, IValidationBlockErrors } from '~/utils/block.utils';
import type { SignatureBlock } from '~/models/blocks/signature-block.model';
import type { ContractHTMLBlock } from '~/models/blocks/contract-html-block.model';
import type { ContractBlock } from '~/models/blocks/contract-block.model';
import type { SignaturesBlock } from '~/models/blocks/signatures-block.model';
import type { ChargeContainerBlock } from '~/models/blocks/charge-container-block.model';
import { CHARGE_BLOCK_PROPERTIES } from '~/components/Builder/Block/ChargeBlock/ChargeBlockCommon';
import {
    BlockSelectedDataPayload,
    IMobileAppService,
    InvoiceMobileBaseProps,
    PaymentScheduleMobileEntity,
    UpdateBlockDataPayload,
    InvoiceSummaryMobileProps,
    MobileInvoiceBlockData
} from '~/components/Builder/Mobile/builderMobileApi.types';
import type { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import type { InvoiceHeaderBlock } from '~/models/blocks/invoice-header-block.model';
import { IInvoiceHeaderBlockStyles } from '~/types/block-styles.types';
import type { ServicesBlock } from '~/models/blocks/services-block.model';
import BaseAbstractBuilderApi from '../BaseAbstractBuilderApi';
import { CompositionType, TBlockType } from '~/types/model.types';
import { getCompanyInfoLayouts } from '~/components/Builder/BuilderSidebar/ContainerSidebar/company-info-layout.utils';
import { getCompositionLayouts } from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layout.utils';
import type { ContainerBlock } from '~/models/blocks/container-block.model';
import { IContainerableBlockResponse } from '~/types/server.types';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import { getInvoiceTypeForAnalytics } from '~/components/common/InvoiceBlock/invoice.utils';
import {
    getPagesProperties,
    isDeleteDisabledOnPage,
    sendPaymentUpdateBlock,
    setPaymentScheduleBlockData,
    setPropsOnInvoiceBlock
} from '~/components/Builder/Mobile/builderMobileApi.utils';
import type { PaymentScheduleBlock } from '~/models/blocks/payment-schedule-block.model';
import type { ScheduleContainerBlock } from '~/models/blocks/schedule-container-block.model';
import { FlowMXContextValue } from '~/components/Builder/FlowMX.provider';

class BuilderMobileCoreApiHandler extends BaseAbstractBuilderApi {
    constructor(
        rootStore: RootStore,
        flowMXContext: FlowMXContextValue,
        MobileAppService: IMobileAppService
    ) {
        super(rootStore, flowMXContext, MobileAppService);
        Object.values(CoreInboundEvents).forEach(coreInboundEvent => {
            this.subscribeToEvent(coreInboundEvent);
        });
    }

    // INBOUND
    [CoreInboundEvents.UpdateBlock]({
        block
    }: {
        block: IContainerableBlockResponse;
    }) {
        const {
            blockCtrl,
            builderUiStore: { selectedFlow, showInvoiceOverlayForId }
        } = this.rootStore;
        const oldBlock = selectedFlow.draftRevision.blocksMap[
            block.block_id
        ] as TBlock;

        blockCtrl.updateBlock(
            oldBlock,
            omit(block, 'usedSigneeIds', 'canDeleteSignature', 'members')
        );

        const blockData: UpdateBlockDataPayload = {
            ...oldBlock.getDataForServer()
        };

        if (oldBlock.type === 'signature_block') {
            blockData.canDeleteSignature =
                oldBlock.parent?.childBlocks.length > 2;
            blockData.members = selectedFlow.members;
            const usedSigneeIds = (oldBlock.parent as SignaturesBlock)
                .assignedSignaturesMemberIds;
            blockData.usedSigneeIds = usedSigneeIds;
        }

        if (oldBlock.type === 'invoice_container_block') {
            const {
                builderUiStore: {
                    selectedFlow: { invoiceContainerBlock }
                }
            } = this.rootStore;
            setPropsOnInvoiceBlock(
                blockData as MobileInvoiceBlockData,
                showInvoiceOverlayForId,
                invoiceContainerBlock,
                selectedFlow
            );
        }

        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.UpdateBlockData,
            {
                block: blockData
            }
        );
    }

    [CoreInboundEvents.UnselectAll]() {
        const { flowCtrl } = this.rootStore;
        flowCtrl.selectBlock();
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.UnselectAllDone,
            {}
        );
    }

    [CoreInboundEvents.UnselectBlock]({
        shouldNotSelectRoot = false
    }: {
        shouldNotSelectRoot?: boolean;
    }) {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;

        const rootLevelParentId =
            this.rootStore.builderUiStore.selectedBlock?.calculateRootLevelParent()
                ?.block_id;
        // (shouldNotSelectRoot = true) is passed when service image action sheet is
        // opened inside invoice block. Since the invoice is already selected -
        // we don't want to select the root-block (cause it will close the editable mode)
        if (shouldNotSelectRoot) {
            this[CoreOutboundEvents.BlockSelected]({
                blockId: selectedFlow.invoiceContainerBlock.block_id,
                isMultiStageFinished: true
            });
        } else {
            this.rootStore.flowCtrl.selectBlock({ blockId: rootLevelParentId });
            if (rootLevelParentId) {
                this[CoreOutboundEvents.BlockSelected]({
                    blockId: rootLevelParentId,
                    isMultiStageFinished: true
                });
            } else {
                this[CoreOutboundEvents.BlockUnselected]();
            }
        }
    }

    [CoreInboundEvents.OnReorderItem]({
        blockId,
        order
    }: {
        blockId: string;
        order: number;
    }) {
        const {
            blockCtrl,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const block = selectedFlow.draftRevision.blocksMap[blockId] as TBlock;

        blockCtrl.moveBlock(block, block.parent as ContainerBlock, order);
    }

    [CoreInboundEvents.GoToBlock]({ blockId }: { blockId: string }) {
        const {
            builderUiStore: { selectAndScrollToBlock, selectedFlow }
        } = this.rootStore;
        const block = selectedFlow.draftRevision.blocksMap[blockId] as TBlock;
        selectAndScrollToBlock({ block, focusBlock: true });
        this[CoreOutboundEvents.BlockSelected]({
            blockId: blockId
        });
    }

    [CoreInboundEvents.MoveBlock]({
        blockId,
        direction,
        isToFirstLast
    }: {
        blockId: string;
        direction: TBlockCreationDirection;
        isToFirstLast: boolean;
    }) {
        const {
            builderUiStore: { selectedPage, selectedFlow }
        } = this.rootStore;
        const block = selectedFlow.draftRevision.blocksMap[blockId] as TBlock;

        const parentBlock = (selectedFlow.draftRevision.blocksMap[
            block.parent_id
        ] || selectedPage.rootBlock) as ContainerBlock;

        const childLayouts = parentBlock.sortedChildLayouts;

        const blockIndex = childLayouts.findIndex(
            item => item.block_id === block.block_id
        );

        const firstLastIndex =
            direction === 'top' ? 0 : childLayouts.length - 1;
        const nextBlockIndex = blockIndex + (direction === 'top' ? -1 : 1);
        const nextBlockIndexFinal = isToFirstLast
            ? firstLastIndex
            : nextBlockIndex;
        const nextBlock = childLayouts[nextBlockIndexFinal]
            ? selectedFlow.draftRevision.blocksMap[
                  childLayouts[nextBlockIndexFinal].block_id
              ]
            : undefined;

        if (nextBlock) {
            this.rootStore.blockCtrl.moveBlock(
                block,
                parentBlock,
                getNewBlockOrder(nextBlock, direction)
            );
        }
    }

    [CoreInboundEvents.DeleteBlock]({ blockId }: { blockId: string }) {
        const {
            toastStore,
            builderUiStore: { selectedFlow, selectedBlock },
            blockCtrl
        } = this.rootStore;
        const block = selectedFlow.draftRevision.blocksMap[blockId] as TBlock;
        const sendPaymentUpdateCallback = () => {
            sendPaymentUpdateBlock(
                selectedFlow,
                selectedBlock,
                this.MobileAppService
            );
        };
        if (block) {
            if (block.isPaymentBlock) {
                blockCtrl.deleteBlock(block, {
                    applyResolvedCallback: () => sendPaymentUpdateCallback()
                });
            } else {
                blockCtrl.deleteBlock(block);
            }
        } else {
            toastStore.add({
                type: ToastType.Error,
                text: 'Failed to delete block. Please reload and try again.'
            });
        }
        if (!block.isPaymentBlock) {
            this[CoreOutboundEvents.PageLoaded]();
        }
    }

    [CoreInboundEvents.CreateBlock]({
        type,
        siblingId,
        layoutType = undefined,
        compositionType = undefined
    }: {
        type: TBlockType;
        siblingId: string;
        layoutType: string;
        compositionType: CompositionType;
    }) {
        const {
            builderUiStore: { selectedPage, selectedFlow },
            pageCtrl
        } = this.rootStore;
        if (
            type === 'contract_block' &&
            (selectedPage.contractBlock ||
                selectedPage.invoiceContainerBlock ||
                selectedPage.serviceSelectionBlocks.length > 0)
        ) {
            pageCtrl.createPageAndBlockAction({
                pageOptions: {
                    pageTitle: 'Contract'
                },
                blockType: 'contract_block'
            });
        } else {
            const siblingBlock = siblingId
                ? selectedFlow.draftRevision.blocksMap[siblingId]
                : last(selectedPage.rootBlock.childBlocks);

            const layout =
                layoutType &&
                (compositionType === CompositionType.CompanyInfo
                    ? getCompanyInfoLayouts(selectedFlow.company).layouts[
                          layoutType
                      ].layoutStruct
                    : getCompositionLayouts().layouts[layoutType].layoutStruct);

            this.rootStore.blockCtrl.createBlock({
                block: siblingBlock,
                blockType: type,
                direction: 'bottom',
                layout,
                compositionType
            });
        }

        this[CoreOutboundEvents.PageLoaded]();
    }

    // OUTBOUND

    [CoreOutboundEvents.BlockSelected]({
        blockId,
        isMultiStageFinished,
        isPaymentScheduleUpdate = false
    }: {
        blockId: string;
        isMultiStageFinished?: boolean;
        isPaymentScheduleUpdate?: boolean;
    }) {
        const {
            builderUiStore: {
                selectedFlow,
                selectedBlock,
                showContractOverlayForId
            },
            analyticsService
        } = this.rootStore;
        const block = selectedFlow.draftRevision.blocksMap[blockId] as TBlock;
        const blockData: BlockSelectedDataPayload = {
            ...block.getDataForServer()
        };

        if (block.type === 'contract_block') {
            blockData.isBlockOverlayOn =
                showContractOverlayForId === block.block_id;
            blockData.flowContainsInvoice = selectedFlow.hasInvoiceBlock;
            blockData.isContractDisabled = (
                block as ContractBlock
            ).hasClientSignatures;
            blockData.assignedSignaturesMembers = (<ContractBlock>(
                block
            )).signaturesBlock.assignedSignaturesMembers;
            blockData.isDeleteDisabled = (
                block as ContractBlock
            ).hasClientSignatures;
        }

        if (block.type === 'invoice_container_block') {
            const {
                builderUiStore: {
                    selectedFlow: {
                        is_template: isTemplate,
                        is_published: isPublished,
                        isOverlayShownForInvoice,
                        invoiceContainerBlock: {
                            isRecurringInvoice,
                            hasRecurringOrScheduledPaidPayments
                        }
                    },
                    showInvoiceOverlayForId
                }
            } = this.rootStore;
            setPropsOnInvoiceBlock(
                blockData as MobileInvoiceBlockData,
                showInvoiceOverlayForId,
                block as InvoiceBlock,
                selectedFlow
            );
            const isRecurringAndPublishedInvoice =
                isRecurringInvoice && isPublished;
            const isReusingBlockAvailable =
                isTemplate ||
                (!isRecurringAndPublishedInvoice &&
                    !hasRecurringOrScheduledPaidPayments);
            blockData.isReuseInvoiceAvailable = isReusingBlockAvailable;
            blockData.isOverlayShownForInvoice = isOverlayShownForInvoice;
            blockData.invoice_column_header_block = pick(
                (block as InvoiceBlock).invoiceColumnHeaderBlock,
                InvoiceMobileBaseProps
            );

            if (!isRecurringInvoice) {
                blockData.isAllPaymentsPaid = (
                    block as InvoiceBlock
                ).invoicePaymentScheduleBlock?.hasCompletedAllPayments;
            }
        }

        if (block.type === 'invoice_header_block') {
            blockData.billToUser = (block as InvoiceHeaderBlock).billToUser;
            const { show_bill_to } = block.styles as IInvoiceHeaderBlockStyles;
            delete blockData.invoice_header;
            blockData.isDisabledDropdown =
                !show_bill_to || selectedFlow.is_template;
        }
        if (block.type === 'services_block') {
            blockData.invoice_column_header_block = pick(
                (block as ServicesBlock).invoice.invoiceColumnHeaderBlock,
                InvoiceMobileBaseProps
            );
            blockData.invoice_summary_block = pick(
                (block as ServicesBlock).invoice.invoiceSummaryBlock,
                InvoiceSummaryMobileProps
            );
            analyticsService.track(
                AnalyticsEvents.BuilderInvoiceDisplaySelected,
                {
                    action: 'action',
                    paid: (block as ServicesBlock).invoice.hasPaidPayments
                        ? 'yes'
                        : 'no',
                    type: getInvoiceTypeForAnalytics(
                        (block as ServicesBlock).invoice
                    ),
                    status: selectedFlow.status,
                    published: selectedFlow.is_published ? 'yes' : 'no',
                    expired: selectedFlow.expired ? 'yes' : 'no'
                }
            );
        }

        if (block.type === 'contract_html_block') {
            blockData.isPaymentsRecapDisabled = (
                selectedBlock as ContractHTMLBlock
            ).isPaymentsRecapDisabled;
            blockData.isServicesRecapDisabled = (
                selectedBlock as ContractHTMLBlock
            ).isServicesRecapDisabled;
        }

        if (block.type === 'payment_schedule_block') {
            setPaymentScheduleBlockData({
                blockData: blockData as Partial<PaymentScheduleMobileEntity>,
                selectedFlow,
                paymentScheduleBlock: selectedBlock as PaymentScheduleBlock
            });
            blockData.isPaymentScheduleUpdate = isPaymentScheduleUpdate;
        }

        if (block.type === 'recurring_schedule_block') {
            blockData.isPaymentScheduleBlockDisabled =
                (selectedBlock.parent as InvoiceBlock)
                    .hasServicesSelectionBlocks ||
                (selectedBlock.parent as InvoiceBlock).isRecurringInvoice;
        }

        if (block.isChargeContainerBlock) {
            // charge block is the child of chargeBlockContainer. we add these props
            // so that the mobile app can update the charge block sheet
            blockData.chargeBlock = pick(
                (block as ChargeContainerBlock).chargeBlock,
                CHARGE_BLOCK_PROPERTIES
            );
            blockData.chargeBlock.isDeleteDisabled =
                selectedFlow.invoiceContainerBlock?.isDeleteDisabled;
            const {
                flow: {
                    invoiceContainerBlock: {
                        isRecurringInvoice: isRecurringInvoiceInCharge,
                        isRecurringUnder10Days
                    }
                }
            } = block as ChargeContainerBlock;
            blockData.chargeBlock.isRecurring = isRecurringInvoiceInCharge;
            blockData.chargeBlock.isRecurringUnderMinimumDays =
                isRecurringUnder10Days;
        }

        if (block.type === 'signature_block') {
            const { signature } = <SignatureBlock>block;

            const contractSibling = block.parent.parent.childBlocks.find(
                x => x.type === 'contract_html_block'
            );

            blockData.isContractContentValid = <ContractHTMLBlock>(
                contractSibling?.blockInputValidations
            );

            blockData.canDeleteSignature =
                (<SignaturesBlock>block.parent)?.childBlocks.length > 2;

            blockData.signature = signature;

            const usedSigneeIds = (block.parent as SignaturesBlock)
                .assignedSignaturesMemberIds;
            blockData.usedSigneeIds = usedSigneeIds;
            blockData.members = selectedFlow.members;
        } else if (block.type === 'schedule_container_block') {
            const { isFutureMeeting } = block as ScheduleContainerBlock;
            blockData.isFutureMeeting = isFutureMeeting;
        } else if (
            // on mobile, we need to know which blocks are created in multi stages
            // so we can show the correct action sheet
            // in the future we should add question block here as well, and Service Selection etc
            ['company_info_block', 'container_block'].includes(block.type)
        ) {
            blockData.isMultiStageFinished = isMultiStageFinished;
        }

        // todo: remove isDirty after long enough
        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.BlockSelected,
            {
                block: blockData,
                isDirty: selectedFlow.isDirty
            }
        );
    }

    [CoreOutboundEvents.BlockUnselected]() {
        const {
            builderUiStore: { selectedFlow, selectedPageId },
            actionsManager: {
                actionsStack: { isUndoAvailable, isRedoAvailable }
            },
            analyticsService
        } = this.rootStore;

        if (!selectedFlow.pages) {
            return;
        }

        const pages = getPagesProperties(selectedFlow);
        const selectedPage = pages.find(x => x.page_id === selectedPageId);
        if (!selectedPage) {
            analyticsService?.track(
                AnalyticsEvents.MobileSelectedPageNotNotExistInPages,
                {
                    action: 'action',
                    pageId: selectedPageId
                }
            );
        }
        // todo: remove isDirty after enough users have upgraded mobile verison
        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.BlockUnselected,
            {
                pages,
                isDirty: selectedFlow.isDirty,
                selectedPage: selectedPage || pages[0],
                actionsStack: {
                    isUndoAvailable,
                    isRedoAvailable
                }
            }
        );
    }

    [CoreOutboundEvents.PageLoaded]() {
        const {
            builderUiStore: { selectedFlow, selectedPage },
            actionsManager: {
                actionsStack: { isUndoAvailable, isRedoAvailable }
            }
        } = this.rootStore;
        let isDeleteDisabled = isDeleteDisabledOnPage(
            selectedFlow,
            selectedPage
        );

        if (selectedFlow.pages) {
            const pages = getPagesProperties(selectedFlow);
            const page = {
                ...selectedPage.getDataForServer(),
                isContractPage: !!selectedPage.contractBlock,
                isInvoicePage: selectedPage.isInvoicePage,
                isDeleteDisabled: isDeleteDisabled,
                isPaymentPage: !!selectedPage.payContainerBlock,
                isSchedulerPage: !!selectedPage.scheduleContainerBlock,
                flowContainsInvoice: !!selectedFlow.invoiceContainerBlock,
                flowContainsScheduler: !!selectedFlow.scheduleContainerBlock
            };

            this.MobileAppService.triggerAppEvent(
                CoreOutboundEvents.PageLoaded,
                {
                    pages,
                    isFlowValid: !selectedFlow.hasErrors,
                    selectedPage: page,
                    selectedPageId: page.page_id,
                    isOverlayShownForInvoice:
                        selectedFlow.isOverlayShownForInvoice,
                    isDirty: selectedFlow.isDirty,
                    actionsStack: {
                        isUndoAvailable,
                        isRedoAvailable
                    }
                }
            );
        }
    }

    // Flow Validation Status should support backwards compatibility -
    // that's why we are using undefined as default and not false
    // todo: replace undefined with false in the future
    [CoreOutboundEvents.FlowValidationStatus]({
        isValid,
        shouldPublish = undefined,
        blocksErrors,
        warnings
    }: {
        isValid: boolean;
        shouldPublish: boolean;
        blocksErrors: IValidationBlockErrors[];
        warnings: string[];
    }) {
        const { flowCtrl } = this.rootStore;
        // unselect all
        flowCtrl.selectBlock();
        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.FlowValidationStatus,
            {
                isValid,
                shouldPublish,
                blocksErrors,
                warnings
            }
        );
    }
}

export default BuilderMobileCoreApiHandler;
